import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_ImageToVideo = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/image-to-video',
			name: 'Image to Video AI — Animate Any Photo Online by Lift',
			description:
				'Turn any image to video with AI. Upload a photo, describe the motion, pick a video model, and watch your picture come to life as a shareable clip. Try Lift today!',
			url: 'https://liftapp.ai/image-to-video',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/image-to-video#product' },
				{ '@id': 'https://liftapp.ai/image-to-video#howto' },
				{ '@id': 'https://liftapp.ai/image-to-video#benefits' },
				{ '@id': 'https://liftapp.ai/image-to-video#faq' },
				{ '@id': 'https://liftapp.ai/image-to-video#why-lift' },
				{ '@id': 'https://liftapp.ai/image-to-video#explore-tools' },
				{ '@id': 'https://liftapp.ai/image-to-video#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/image-to-video#product',
			name: 'Image to Video AI',
			description:
				'Turn any image to video with AI. Upload a photo, describe the motion, pick a video model, and watch your picture come to life as a shareable clip.',
			url: 'https://liftapp.ai/image-to-video',
			fileFormat: ['video/mp4'],
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/image-to-video#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload Your Image',
					text: 'Add the photo you want to animate as the start frame — and optionally an end frame or a reference image for extra control.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Describe The Motion',
					text: 'Write a prompt that tells the AI how your picture should move, or tap Improve Prompt for a more detailed description.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Pick A Model And Settings',
					text: 'Choose an AI video model and set the aspect ratio, resolution, duration, and audio to match where your clip will live.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download And Use',
					text: 'Download your video or publish it straight to your social feeds.',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-to-video#benefits',
			name: 'Who can benefit from Image to Video AI?',
			description:
				"Lift's Image to Video AI helps content creators, e-commerce sellers, families, marketers, and photographers turn photos into ready-to-share video clips.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Content creators — Give still shots a second life. One good photo becomes a moving post for Instagram, TikTok, or YouTube — no reshoots, no editing apps.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'E-commerce sellers — Turn product stills into dynamic clips that show texture, angle, and detail — motion sells what a flat image only suggests.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Memory keepers & families — Bring favorite pictures to life: a birthday, a wedding, an old family photo that suddenly moves again.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Marketers & SMM managers — Stretch one photoshoot into a whole set of animated creatives. Change the prompt, not the budget.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Photographers & visual artists — Add motion to portraits and landscapes to create cinemagraph-style pieces that stand out in any portfolio.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Image to Video AI Examples by Lift',
				description:
					'Photo to video AI clips created with the Lift image to video generator.',
			},
		},
		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/image-to-video#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift Image to Video AI?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift Image to Video AI is a premium online tool that turns a still picture into a short video. Your photo sets the scene, your prompt directs the motion, and a lineup of leading AI video models handles the animation.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift Image to Video AI work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload your picture as the start frame, describe how it should move, and choose a model and settings. Hit Generate and your image comes back as a moving clip in moments — no editing experience needed.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I control how the video starts and ends?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Set your photo as the start frame and add a second image as the end frame — the AI animates the transition between them, so you decide exactly where the clip begins and where it lands.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All videos generated in Lift can be exported without watermark when you're on the Pro plan. Download them in high resolution — ready for social media, ads, or presentations.",
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to upload my photos to Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — Lift processes your photos securely and uses them only to generate your results.',
					},
				},
			],
		},
		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-to-video#why-lift',
			name: 'Why Lift Image to Video AI stands out',
			description:
				"Lift's Image to Video AI combines professional-grade results, intelligent rendering, cross-platform access, and affordability.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Why Choose the Image to Video AI by Lift',
				description: 'AI image to video results created with Lift.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-to-video#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			itemListElement: [...exploreSchemaItemList.itemListElement],
		},
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/image-to-video#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Image to Video AI',
					item: 'https://liftapp.ai/image-to-video',
				},
			],
		},
	],
};
